import React, { useState, useEffect } from "react";
import { useAuth } from "../../Utils/Auth";
import { toast } from "react-toastify";
import AccountService from "../../Services/AccountService";

const IntroducerSettingModal = ({
  show,
  onClose,
  userId,
  fetchData,
  userData,
}) => {
  const auth = useAuth();

  const [status, setStatus] = useState("active");
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [withdrawEnabled, setWithdrawEnabled] = useState(false);
  const [depositPercentage, setDepositPercentage] = useState("");
  const [withdrawPercentage, setWithdrawPercentage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userData) {
      setStatus(
        Number(userData.isTransfer) === 1
          ? "active"
          : "inactive"
      );

      setDepositEnabled(
        Number(userData.isDeposit) === 1
      );

      setWithdrawEnabled(
        Number(userData.isWithdraw) === 1
      );

      setDepositPercentage(
        userData.depositpercentage || ""
      );

      setWithdrawPercentage(
        userData.withdrawpercentage || ""
      );
    }
  }, [userData]);

  if (!show) return null;

  const isInactive = status === "inactive";

  // Only status UI change
  const handleStatusChange = (type) => {
    setStatus(type);
  };

  // Deposit / Withdraw same as before
  const handleStashToggle = async (
    type,
    currentStatus
  ) => {
    if (isInactive) return;

    try {
      const newStatus = !currentStatus;

      if (type === "Deposit") {
        setDepositEnabled(newStatus);
      } else {
        setWithdrawEnabled(newStatus);
      }

      const payload = {
        id: Number(userId),
        type,
        status: newStatus,
      };

      await AccountService.introducerDepositeWithdraw(
        payload,
        auth.user
      );

      toast.success(`${type} Updated`);
    } catch (error) {
      toast.error(`${type} update failed`);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Status API
      const statusPayload = {
        id: Number(userId),
        status: status === "active",
      };

      await AccountService.commissionUpdate(
        statusPayload,
        auth.user
      );

      // Charges API
      const payload = {
        id: Number(userId),
        depositpercentage:
          Number(depositPercentage) || 0,
        withdrawpercentage:
          Number(withdrawPercentage) || 0,
      };

      await AccountService.addCharges(
        payload,
        auth.user
      );

      // Static Success Message Only
      toast.success("Updated Successfully");

      fetchData();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.errMessage ||
        "Save failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{
        backgroundColor:
          "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div
          className="modal-content border-0 shadow"
          style={{
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0 font-weight-bold">
              Charges Setting
            </h5>

            <button
              className="close border-0 bg-transparent"
              onClick={onClose}
            >
              <span>&times;</span>
            </button>
          </div>

          <div className="p-4 bg-white">
            {/* Status */}
            <div className="mb-4">
              <label className="font-weight-bold d-block mb-2">
                Account Status
              </label>

              <div className="d-flex">
                <button
                  className={`btn flex-fill ${status === "active"
                      ? "btn-success"
                      : "btn-light border"
                    }`}
                  onClick={() =>
                    handleStatusChange(
                      "active"
                    )
                  }
                >
                  Active
                </button>

                <button
                  className={`btn flex-fill ml-2 ${status === "inactive"
                      ? "btn-danger"
                      : "btn-light border"
                    }`}
                  onClick={() =>
                    handleStatusChange(
                      "inactive"
                    )
                  }
                >
                  Inactive
                </button>
              </div>
            </div>

            {/* Deposit */}
            <div className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Deposit Charge
                </h6>

                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="depositToggle"
                    checked={
                      depositEnabled
                    }
                    disabled={isInactive}
                    onChange={() =>
                      handleStashToggle(
                        "Deposit",
                        depositEnabled
                      )
                    }
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="depositToggle"
                  ></label>
                </div>
              </div>

              <input
                type="number"
                className="form-control"
                placeholder="Enter deposit %"
                value={
                  depositPercentage
                }
                disabled={
                  isInactive ||
                  !depositEnabled
                }
                onChange={(e) =>
                  setDepositPercentage(
                    e.target.value
                  )
                }
              />
            </div>

            {/* Withdraw */}
            <div className="mb-4 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Withdraw Charge
                </h6>

                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="withdrawToggle"
                    checked={
                      withdrawEnabled
                    }
                    disabled={isInactive}
                    onChange={() =>
                      handleStashToggle(
                        "Withdrawal",
                        withdrawEnabled
                      )
                    }
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="withdrawToggle"
                  ></label>
                </div>
              </div>

              <input
                type="number"
                className="form-control"
                placeholder="Enter withdraw %"
                value={
                  withdrawPercentage
                }
                disabled={
                  isInactive ||
                  !withdrawEnabled
                }
                onChange={(e) =>
                  setWithdrawPercentage(
                    e.target.value
                  )
                }
              />
            </div>

            {/* Save */}
            <button
              className="btn btn-primary btn-block py-2 font-weight-bold"
              style={{
                borderRadius: "12px",
              }}
              onClick={handleSave}
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : "Save Settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroducerSettingModal;